
import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { ProductComponent } from '@theme/view-event-elements';
import {
  debounce,
  isDesktopBreakpoint,
  mediaQueryLarge,
  yieldToMainThread,
} from '@theme/utilities';
import { SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';

export class ProductCardLink extends ProductComponent {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  handleViewTransition(event) {
    if (event.defaultPrevented) return;

    if (event.target instanceof Element) {
      const interactive = event.target.closest(
        'button, input, label, select, [tabindex="1"]'
      );
      if (interactive) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;

    if (
      !cardGallery ||
      !cardGallery.hasAttribute('data-view-transition-to-main-product')
    ) return;

    const { imagesToTransition } = this.refs;

    const activeImage =
      imagesToTransition?.find(
        image =>
          image.closest('slideshow-slide')
            ?.getAttribute('aria-hidden') === 'false'
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) {
      this.#setImageSrcset(activeImage);
    }

    cardGallery.setAttribute(
      'data-view-transition-type',
      'product-image-transition'
    );
    cardGallery.setAttribute(
      'data-view-transition-triggered',
      'true'
    );
  }

  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);
    const rawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(rawUrl)) {
      const fade = image.animate(
        [{ opacity: 0.8 }, { opacity: 1 }],
        { duration: 125, easing: 'ease-in-out' }
      );

      fade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    const link = this.refs.productCardLink;
    if (!link.getAttribute('href')) return '';

    const url = new URL(link.href);
    const override = this.#getProductPageVariantOverride();

    if (override?.action === 'set') {
      url.searchParams.set('variant', override.variantId);
    } else if (override?.action === 'remove') {
      url.searchParams.delete('variant');
    }

    return url.toString();
  }

  getSelectedVariantId() {
    const override = this.#getProductPageVariantOverride();

    return override?.action === 'set'
      ? override.variantId
      : null;
  }

  #getProductPageVariantOverride() {
    const link = this.refs.productCardLink;

    if (link.getAttribute('href')) {
      const url = new URL(link.href);
      if (url.searchParams.has('variant')) return null;
    }

    const variantId = this.#getCheckedVariantId();
    if (variantId === undefined) return null;

    return variantId
      ? { action: 'set', variantId }
      : { action: 'remove' };
  }

  #getCheckedVariantId() {
    const input = this.querySelector(
      'input[type="radio"]:checked[data-variant-id]'
    );
    return input?.dataset.variantId;
  }

  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  #navigateToURL = (event, url) => {
    const newTab =
      event instanceof MouseEvent &&
      (
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.button === 1
      );

    if (newTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    }

    window.location.href = url.href;
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;

    if (!(link instanceof HTMLAnchorElement)) {
      throw new Error('Product card link not found');
    }

    this.#handleQuickAdd();

    this.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect
    );

    this.addEventListener(
      SlideshowSelectEvent.eventName,
      this.#handleSlideshowSelect
    );

    mediaQueryLarge.addEventListener(
      'change',
      this.#handleQuickAdd
    );

    this.addEventListener('click', this.navigateToProduct);

    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect
    );

    this.removeEventListener(
      SlideshowSelectEvent.eventName,
      this.#handleSlideshowSelect
    );

    mediaQueryLarge.removeEventListener(
      'change',
      this.#handleQuickAdd
    );

    this.removeEventListener('click', this.navigateToProduct);

    this.removeEventListener(
      'pointerenter',
      this.#fetchProductPageHandler
    );

    this.removeEventListener(
      'focusin',
      this.#fetchProductPageHandler
    );
  }

  #preloadNextPreviewImage() {
    const slideshow = this.refs.slideshow;
    const current = slideshow?.slides?.[slideshow?.current];

    current?.nextElementSibling
      ?.querySelector('img[loading="lazy"]')
      ?.removeAttribute('loading');
  }

  #handleQuickAdd = () => {
    this.removeEventListener(
      'pointerenter',
      this.#fetchProductPageHandler
    );
    this.removeEventListener(
      'focusin',
      this.#fetchProductPageHandler
    );

    if (isDesktopBreakpoint()) {
      this.addEventListener(
        'pointerenter',
        this.#fetchProductPageHandler
      );
      this.addEventListener(
        'focusin',
        this.#fetchProductPageHandler
      );
    }
  };

  #handleProductSelect = event => {
    const {
      optionValueId,
      variantId,
      connectedProductUrl,
    } = event.detail ?? {};

    if (optionValueId && event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(optionValueId);
    }

    if (typeof variantId === 'string') {
      this.applyVariantToLinks(
        variantId,
        typeof connectedProductUrl === 'string'
          ? connectedProductUrl
          : undefined
      );
    }

    if (!event.promise?.then) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;

        this.updatePrice(html, variantId);
        this.#isUnavailableVariantSelected(html);
        this.#updateProductUrl(
          html,
          typeof variantId === 'string'
            ? variantId
            : undefined
        );

        this.refs.quickAdd?.fetchProductPage(
          this.productPageUrl
        );

        if (event.target !== this.variantPicker) {
          this.variantPicker?.updateVariantPicker(html);
        }

        this.#updateVariantImages();
        this.#previousSlideIndex = null;

        this.removeAttribute('data-no-swatch-selected');
        this.#updateOverflowList();
      })
      .catch(error => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[product-card] Event promise rejected:',
            error
          );
        }
      });
  };

  #updateOverflowList() {
    const overflowList = this.querySelector(
      'swatches-variant-picker-component overflow-list'
    );

    if (!overflowList?.querySelector('[slot="overflow"]')) {
      return;
    }

    requestAnimationFrame(() => {
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  updatePrice(html, variantId) {
    const priceContainer = this.querySelector(
      "product-price [ref='priceContainer']"
    );

    if (!priceContainer) return;

    const newPrice = html.querySelector(
      "product-price [ref='priceContainer']"
    );

    if (newPrice && newPrice.textContent.trim()) {
      morph(priceContainer, newPrice);
      return;
    }

    if (variantId) {
      console.debug(
        '[DLN] Empty product-card price response:',
        variantId
      );
    }
  }

  #updateProductUrl(html, intendedVariantId) {
    const responseCard = html.querySelector('product-card');
    const anchor = responseCard?.querySelector('a');

    const featuredMediaUrl = responseCard?.getAttribute(
      'data-featured-media-url'
    );

    if (featuredMediaUrl) {
      this.setAttribute(
        'data-featured-media-url',
        featuredMediaUrl
      );
    }

    if (anchor instanceof HTMLAnchorElement) {
      if (anchor.getAttribute('href')?.trim() === '') return;

      const responseUrl = new URL(anchor.href);

      this.#updateLinks({
        pathname: responseUrl.pathname,
        variantId:
          intendedVariantId ??
          responseUrl.searchParams.get('variant'),
      });
    }
  }

  applyVariantToLinks(variantId, productUrl) {
    let pathname;

    if (productUrl) {
      try {
        pathname = new URL(
          productUrl,
          window.location.origin
        ).pathname;
      } catch (error) {
        console.warn('[product-card] Invalid URL:', error);
        return;
      }
    }

    this.#updateLinks({ pathname, variantId });
  }

  #updateLinks({ pathname, variantId }) {
    const {
      productCardLink,
      productTitleLink,
      cardGalleryLink,
    } = this.refs;

    for (const link of [
      productCardLink,
      cardGalleryLink,
      productTitleLink,
    ]) {
      if (!(link instanceof HTMLAnchorElement) || !link.href) {
        continue;
      }

      const url = new URL(link.href);

      if (pathname) url.pathname = pathname;

      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }

      link.href = url.toString();
    }
  }

  #isUnavailableVariantSelected(html) {
    for (const variant of html.querySelectorAll('input:checked')) {
      this.#toggleAddToCartButton(
        variant.dataset.optionAvailable === 'true'
      );
    }
  }

  #toggleAddToCartButton(enable) {
    const button = this.querySelector(
      '.add-to-cart__button button'
    );

    if (button instanceof HTMLButtonElement) {
      button.disabled = !enable;
    }
  }

  #updateVariantImages() {
    const { slideshow } = this.refs;
    const selected = this.variantPicker?.selectedOption;

    if (!slideshow || !selected) return;

    const selectedImageId = selected.dataset.optionMediaId;
    if (!selectedImageId) return;

    this.dlnClearPreview();

    const slides = slideshow.refs?.slides || [];

    for (const slide of slides) {
      if (slide.getAttribute('variant-image') == null) {
        continue;
      }

      slide.hidden =
        slide.getAttribute('slide-id') !== selectedImageId;
    }

    slideshow.select(
      { id: selectedImageId },
      undefined,
      { animate: false }
    );
  }

  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  get variantPicker() {
    return this.querySelector(
      'swatches-variant-picker-component'
    );
  }

  #previousSlideIndex = null;
  #dlnForcedPreview = false;

  #handleSlideshowSelect = event => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  // DLN: toon een specifieke afbeelding direct.
  #dlnShowMedia(id) {
    const slideshow = this.refs.slideshow;

    if (!slideshow || !id) return false;

    const slides = [
      ...slideshow.querySelectorAll('slideshow-slide'),
    ];

    const target = slides.find(
      slide =>
        slide.getAttribute('slide-id') === String(id)
    );

    if (!target) return false;

    slides.forEach(slide => {
      const active = slide === target;

      slide.hidden = !active;
      slide.style.display = active ? 'block' : 'none';
      slide.style.visibility = active ? 'visible' : 'hidden';
      slide.style.opacity = active ? '1' : '0';

      slide.setAttribute(
        'aria-hidden',
        active ? 'false' : 'true'
      );
    });

    return true;
  }

  // Hover verandert de afbeelding, niet de gekozen variant.
  previewVariant(id) {
    if (!id) return;

    this.resetVariant.cancel();

    this.#dlnForcedPreview = this.#dlnShowMedia(id);
  }

  dlnClearPreview() {
    const slideshow = this.refs.slideshow;
    if (!slideshow) return;

    for (const slide of slideshow.querySelectorAll('slideshow-slide')) {
      slide.style.removeProperty('display');
      slide.style.removeProperty('visibility');
      slide.style.removeProperty('opacity');
    }

    this.#dlnForcedPreview = false;
  }

  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;
    if (!slideshow) return;

    // Laat een actieve swatch-preview met rust.
    if (this.#dlnForcedPreview) return;

    this.resetVariant.cancel();

    if (
      this.#previousSlideIndex != null &&
      this.#previousSlideIndex > 0
    ) {
      slideshow.select(
        this.#previousSlideIndex,
        undefined,
        { animate: false }
      );
    } else {
      slideshow.next(
        undefined,
        { animate: false }
      );

      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;

      slideshow.previous(
        undefined,
        { animate: false }
      );
    } else {
      this.resetVariant();
    }
  }

  #resetVariant = () => {
    const slideshow = this.refs.slideshow;
    if (!slideshow) return;

    const selectedId =
      this.variantPicker?.selectedOption?.dataset.optionMediaId;

    const initialId =
      slideshow.initialSlide?.getAttribute('slide-id');

    const targetId = selectedId || initialId;

    if (targetId) {
      this.#dlnShowMedia(targetId);
    }

    this.#dlnForcedPreview = false;
  };

  resetVariant = debounce(this.#resetVariant, 100);

  navigateToProduct = event => {
    if (!(event.target instanceof Element)) return;
    if (this.hasAttribute('data-no-navigation')) return;

    const interactive = event.target.closest(
      'button, input, label, select, [tabindex="1"]'
    );

    if (interactive) return;

    const link = this.refs.productCardLink;
    if (!link?.href) return;

    const linkURL = new URL(link.href);
    const anchor = link.getAttribute('id');

    if (!anchor) return;

    const infiniteResultsList = this.closest(
      'results-list[infinite-scroll="true"]'
    );

    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');

      url.hash = anchor;

      if (parent?.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    if (!event.target.closest('a')) {
      this.#navigateToURL(event, linkURL);
    }
  };

  updateDLNStock(variantId) {
    if (!variantId) return;

    const inventoryScript = this.querySelector(
      '[data-dln-card-inventory]'
    );

    const stockMessage = this.querySelector(
      '[data-dln-card-stock]'
    );

    if (!inventoryScript || !stockMessage) return;

    let inventoryData = {};

    try {
      inventoryData = JSON.parse(
        inventoryScript.textContent || '{}'
      );
    } catch (error) {
      console.warn('[DLN] Inventory parse error:', error);
      stockMessage.hidden = true;
      return;
    }

    const variant = inventoryData[String(variantId)];

    if (!variant) {
      stockMessage.hidden = true;
      return;
    }

    const quantity = Number(variant.quantity);

    const shouldShow =
      variant.tracked === true &&
      variant.available === true &&
      quantity > 0 &&
      quantity <= 2;

    if (!shouldShow) {
      stockMessage.hidden = true;
      return;
    }

    const stockText = stockMessage.querySelector(
      '[data-dln-card-stock-text]'
    );

    const stockDot = stockMessage.querySelector(
      '.dln-card-stock__dot'
    );

    const isDutch =
      document.documentElement.lang
        ?.toLowerCase()
        .startsWith('nl') ?? false;

    if (stockText) {
      stockText.textContent =
        quantity === 1
          ? isDutch
            ? 'Nog maar 1 op voorraad'
            : 'Only 1 left'
          : isDutch
            ? 'Nog maar 2 op voorraad'
            : 'Only 2 left';
    }

    if (stockDot) {
      stockDot.classList.remove(
        'dln-card-stock__dot--last',
        'dln-card-stock__dot--limited'
      );

      stockDot.classList.add(
        quantity === 1
          ? 'dln-card-stock__dot--last'
          : 'dln-card-stock__dot--limited'
      );
    }

    stockMessage.hidden = false;
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

class SwatchesVariantPickerComponent extends VariantPicker {
  dlnHoverInput = null;
  dlnHoverPrice = null;

  dlnFindSwatch(target) {
    if (!(target instanceof Element)) return null;

    const label = target.closest('label');

    const input =
      label?.querySelector(
        'input[type="radio"][data-dln-variant-price]'
      ) ||
      target.closest(
        'input[type="radio"][data-dln-variant-price]'
      );

    return input instanceof HTMLInputElement &&
      this.contains(input)
      ? input
      : null;
  }

  dlnRestoreHover = () => {
    if (!this.dlnHoverInput) return;

    if (this.dlnHoverPrice?.element?.isConnected) {
      this.dlnHoverPrice.element.textContent =
        this.dlnHoverPrice.text;
    }

    this.dlnHoverInput = null;
    this.dlnHoverPrice = null;

    this.closest('product-card')?.resetVariant?.();
  };

  dlnPreviewHover = event => {
    if (event.pointerType !== 'mouse') return;

    const input = this.dlnFindSwatch(event.target);

    if (!input || input === this.dlnHoverInput) return;

    this.dlnRestoreHover();

    const card = this.closest('product-card');
    if (!card) return;

    const price = card.querySelector(
      "product-price [ref='priceContainer'] .price"
    );

    if (price && input.dataset.dlnVariantPrice) {
      this.dlnHoverPrice = {
        element: price,
        text: price.textContent,
      };

      price.textContent = input.dataset.dlnVariantPrice;
    }

    this.dlnHoverInput = input;

    const mediaId = input.dataset.optionMediaId;

    if (mediaId) {
      card.previewVariant(mediaId);
    }
  };

  dlnEndHover = event => {
    if (event.pointerType !== 'mouse') return;

    const input = this.dlnFindSwatch(event.target);

    if (!input || input !== this.dlnHoverInput) return;

    if (this.dlnFindSwatch(event.relatedTarget) === input) {
      return;
    }

    this.dlnRestoreHover();
  };

  dlnCommitHover = event => {
    if (!(event.target instanceof HTMLInputElement)) return;

    if (!event.target.matches('[data-dln-variant-price]')) {
      return;
    }

    this.dlnHoverInput = null;
    this.dlnHoverPrice = null;
  };

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(
      'pointerover',
      this.dlnPreviewHover
    );

    this.addEventListener(
      'pointerout',
      this.dlnEndHover
    );

    this.addEventListener(
      'change',
      this.dlnCommitHover,
      true
    );

    requestAnimationFrame(() => {
      const checkedSwatch = this.querySelector(
        'input[type="radio"]:checked'
      );

      if (checkedSwatch instanceof HTMLInputElement) {
        this.updateDLNPVDBadge(checkedSwatch);
      }
    });
  }

  disconnectedCallback() {
    this.removeEventListener(
      'pointerover',
      this.dlnPreviewHover
    );

    this.removeEventListener(
      'pointerout',
      this.dlnEndHover
    );

    this.removeEventListener(
      'change',
      this.dlnCommitHover,
      true
    );

    super.disconnectedCallback();
  }

  updateDLNPVDBadge(swatchInput) {
    if (!(swatchInput instanceof HTMLInputElement)) return;

    const productCard = this.closest('product-card');

    const pvdBadge = productCard?.querySelector(
      '[data-dln-pvd-badge]'
    );

    if (!(pvdBadge instanceof HTMLElement)) return;

    const value = (swatchInput.value || '')
      .trim()
      .toLowerCase();

    const isGold =
      value === 'gold' ||
      value === 'goud';

    pvdBadge.hidden = !isGold;
  }

  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const clickedSwatch = event.target;

    const isSwatchInput =
      clickedSwatch instanceof HTMLInputElement &&
      clickedSwatch.name?.includes('-swatch');

    const hasAvailableVariant =
      clickedSwatch.dataset.hasAvailableVariant === 'true';

    const variantId =
      clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    if (
      isSwatchInput &&
      hasAvailableVariant &&
      variantId
    ) {
      event.stopPropagation();

      this.updateSelectedOption(clickedSwatch);

      const optionValueId =
        clickedSwatch.dataset.optionValueId || '';

      const connectedProductUrl =
        clickedSwatch.dataset.connectedProductUrl || '';

      const requestUrl = this.buildRequestUrl(
        clickedSwatch,
        'product-card',
        [optionValueId]
      );

      const dlnVariantPrice =
        clickedSwatch.dataset.dlnVariantPrice || '';

      const productCard = this.closest('product-card');

      this.updateDLNPVDBadge(clickedSwatch);

      const priceContainer = productCard?.querySelector(
        "product-price [ref='priceContainer']"
      );

      if (dlnVariantPrice && priceContainer) {
        const priceElement = priceContainer.querySelector(
          '.price'
        );

        if (priceElement) {
          priceElement.textContent = dlnVariantPrice;
        }
      }

      if (productCard instanceof ProductCard) {
        productCard.updateDLNStock(variantId);
      }

      this.fetchUpdatedSection(
        requestUrl,
        {
          detail: {
            optionValueId,
            variantId,
            connectedProductUrl,
          },
        }
      );

      return;
    }

    super.variantChanged(event);
  }

  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define(
    'swatches-variant-picker-component',
    SwatchesVariantPickerComponent
  );
}
